"use client";

// Ported from relay-protocol packages/relay-widget/src/components/Dropdown.tsx
// (v0.1.0). Mechanical substitution only: CSS Modules `styles.x` references
// become literal "rd-x" class names (see ../styles/component.css's header
// mapping table) and the CSS Modules import becomes the plain side-effect
// import below. One addition beyond the mechanical rename: `data-vaul-no-drag`
// on the listbox <ul>, so a vaul drawer host (e.g. RelayDeposit's mobile
// ResponsiveShell) doesn't mistake a pointer drag over the open popup for a
// drawer-dismiss swipe (see WalletInfoContent.tsx's `wi-scrollable-body` for
// the existing convention this follows). Everything else -- the ARIA
// "Collapsible Dropdown Listbox" pattern, keyboard handling, outside-pointerdown
// close, and the option-click preventDefault() guard against label-forwarded
// re-toggling -- is the original logic, untouched.
//
// Accessible custom dropdown used in place of a native <select> wherever an
// option needs a leading icon (native <select>/<option> can't render
// per-option images/SVGs in most browsers). Implements the ARIA
// "Collapsible Dropdown Listbox" pattern: a single-select popup button whose
// focus never leaves the trigger -- the currently-highlighted option is
// communicated via `aria-activedescendant` rather than moving DOM focus,
// which is what lets Enter/Space/Arrow keys keep working straight off the
// button.
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { ChevronDownIcon } from "../../wallet-info/components/icons";
import "../styles/component.css";

export interface DropdownOption {
  value: string;
  label: string;
  icon?: ReactNode;
  /** Optional muted second line under `label` ON THE CLOSED TRIGGER only
   * ("Network", "18 decimals", "on ONEchain", ...). Added by the 2026-07-30
   * redesign: chain and token became two half-width selectors on one row with
   * no field label of their own, so each has to say what it is. Deliberately
   * NOT rendered inside the popup list -- there the options are all the same
   * kind of thing, and a second line per row just makes the list harder to
   * scan. */
  subLabel?: string;
}

export interface DropdownProps {
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  ariaLabel: string;
  id?: string;
}

export function Dropdown({ value, options, onChange, disabled, ariaLabel, id }: DropdownProps) {
  const generatedId = useId();
  const baseId = id ?? generatedId;
  const listboxId = `${baseId}-listbox`;

  const [open, setOpen] = useState(false);
  const selectedIndex = options.findIndex((o) => o.value === value);
  const [activeIndex, setActiveIndex] = useState(selectedIndex >= 0 ? selectedIndex : 0);

  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLLIElement | null)[]>([]);

  const isDisabled = Boolean(disabled) || options.length === 0;
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  // Outside click closes the popup. pointerdown (not click) so it fires
  // before -- and doesn't race -- a click on the trigger button re-opening
  // it in the same interaction.
  useEffect(() => {
    if (!open) return;
    function handlePointerDown(e: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  // Keep the highlighted option scrolled into view as it moves via keyboard.
  // Guarded: jsdom (used in tests) doesn't implement scrollIntoView at all.
  useEffect(() => {
    if (!open) return;
    optionRefs.current[activeIndex]?.scrollIntoView?.({ block: "nearest" });
  }, [open, activeIndex]);

  function openList() {
    if (isDisabled) return;
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  }

  function closeList(refocus: boolean) {
    setOpen(false);
    if (refocus) buttonRef.current?.focus();
  }

  function commit(index: number) {
    const opt = options[index];
    if (!opt) return;
    onChange(opt.value);
    closeList(true);
  }

  function handleButtonClick() {
    if (isDisabled) return;
    if (open) closeList(false);
    else openList();
  }

  function handleButtonKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    if (isDisabled) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!open) openList();
        else setActiveIndex((i) => Math.min(i + 1, options.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        if (!open) openList();
        else setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (!open) openList();
        else commit(activeIndex);
        break;
      case "Escape":
        if (open) {
          e.preventDefault();
          closeList(true);
        }
        break;
      case "Tab":
        // Let Tab's default focus-move happen; just collapse the popup.
        if (open) setOpen(false);
        break;
      default:
        break;
    }
  }

  const activeOptionId = open && options[activeIndex] ? `${baseId}-option-${activeIndex}` : undefined;

  return (
    <div className="rd-dropdown" ref={rootRef}>
      <button
        type="button"
        id={baseId}
        ref={buttonRef}
        className="rd-dropdown-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={activeOptionId}
        aria-label={ariaLabel}
        disabled={isDisabled}
        onClick={handleButtonClick}
        onKeyDown={handleButtonKeyDown}
      >
        <span className="rd-dropdown-trigger-content">
          {selected?.icon}
          <span className="rd-dropdown-trigger-label">
            {selected?.label ?? ""}
            {selected?.subLabel && <small className="rd-dropdown-trigger-sub">{selected.subLabel}</small>}
          </span>
        </span>
        <span className="rd-dropdown-chevron" aria-hidden="true">
          <ChevronDownIcon size={16} />
        </span>
      </button>

      {open && (
        <ul
          className="rd-dropdown-listbox"
          role="listbox"
          id={listboxId}
          aria-label={ariaLabel}
          tabIndex={-1}
          data-vaul-no-drag
        >
          {options.map((opt, index) => {
            const isSelected = opt.value === value;
            const isActive = index === activeIndex;
            return (
              <li
                key={opt.value}
                id={`${baseId}-option-${index}`}
                role="option"
                aria-selected={isSelected}
                ref={(el) => {
                  optionRefs.current[index] = el;
                }}
                className={[
                  "rd-dropdown-option",
                  isActive ? "rd-dropdown-option-active" : "",
                  isSelected ? "rd-dropdown-option-selected" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onPointerEnter={() => setActiveIndex(index)}
                // preventDefault: if a host wraps this dropdown in a <label>,
                // a plain click on this (non-interactive) <li> would trigger
                // the label's activation behavior -- a synthetic click
                // forwarded to the trigger button -- re-toggling the popup
                // open right after selection. Cancelling the default click
                // behavior suppresses that forwarding regardless of host
                // markup.
                onClick={(e) => {
                  e.preventDefault();
                  commit(index);
                }}
              >
                {opt.icon}
                <span className="rd-dropdown-option-label">{opt.label}</span>
                {isSelected && (
                  <span className="rd-dropdown-option-check" aria-hidden="true">
                    ✓
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
